import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getSupplierNoticeForEdit, updateSupplierNotice } from '../../../models/noticesModel';
import { toast } from 'react-toastify';
import WysiwygEditor from '../../../components/common/WysiwygEditor';

function SupplierNoticeEdit() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  
  const [formData, setFormData] = useState({
    notice_title: '',
    notice_text: '',
    notice_status: 0
  });

  useEffect(() => {
    if (user && noticeId) {
      fetchNotice();
    }
  }, [user, noticeId]);

  const fetchNotice = async () => {
    try {
      setLoading(true);
      const response = await getSupplierNoticeForEdit(noticeId);
      
      if (response.status === 1) {
        const notice = response.data?.row || response.notice;
        setFormData({
          notice_title: notice.notice_title || '',
          notice_text: notice.notice_text || '',
          notice_status: notice.notice_status || 0
        });
        setExistingFiles(response.data?.files || []);
      } else {
        throw new Error(response.message || 'Failed to fetch notice');
      }
    } catch (err) {
      console.error('Error fetching notice:', err);
      toast.error(err.message || 'Failed to load notice');
      navigate('/dashboard/notices/supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.notice_title.trim()) {
      toast.error('Notice title is required');
      return;
    }

    if (!formData.notice_text.trim()) {
      toast.error('Notice content is required');
      return;
    }

    try {
      setSaving(true);
      const submitData = {
        id: noticeId,
        notice_title: formData.notice_title,
        notice_text: formData.notice_text,
        notice_status: formData.notice_status,
        files: files
      };
      
      const response = await updateSupplierNotice(submitData);
      
      if (response.status === 1) {
        toast.success('Notice updated successfully');
        navigate(`/dashboard/notices/supplier/view/${noticeId}`);
      } else {
        throw new Error(response.message || 'Failed to update notice');
      }
    } catch (err) {
      console.error('Error updating notice:', err);
      toast.error(err.message || 'Failed to update notice');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = useCallback(() => {
    navigate(`/dashboard/notices/supplier/view/${noticeId}`);
  }, [navigate, noticeId]);

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: 'var(--color-secondary-default)' }}
        ></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Update Notice</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <input type="hidden" name="id" value={noticeId} />
        <input type="hidden" name="notice_status" value={formData.notice_status} />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notice Details</h3>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="notice_title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="notice_title"
                    name="notice_title"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.notice_title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter notice title"
                  />
                </div>

                {/* Existing Files */}
                {existingFiles.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Existing Files
                    </label>
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-300 rounded-md mb-3">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">File</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {existingFiles.map((file, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2 text-sm text-gray-900">{file.file_name}</td>
                            <td className="px-3 py-2 text-sm">
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded"
                                  onClick={() => {
                                    // Download file logic
                                    const form = document.createElement('form');
                                    form.method = 'POST';
                                    form.action = '/files/storage';
                                    const input = document.createElement('input');
                                    input.type = 'hidden';
                                    input.name = 'file';
                                    input.value = file.file_path;
                                    form.appendChild(input);
                                    document.body.appendChild(form);
                                    form.submit();
                                    document.body.removeChild(form);
                                  }}
                                >
                                  Download
                                </button>
                                <button
                                  type="button"
                                  className="text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to remove this file?')) {
                                      // Remove file logic - you'd need to implement this
                                      window.location.href = `/notices/remove_file/${file.file_path}`;
                                    }
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Content */}
                <div>
                  <label htmlFor="notice_text" className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <div className="border border-gray-300 rounded-md" style={{ minHeight: '300px' }}>
                    <WysiwygEditor
                      value={formData.notice_text}
                      onChange={(content) => setFormData(prev => ({
                        ...prev,
                        notice_text: content
                      }))}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div>
                  <button 
                    type="submit" 
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#28a745] hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Attachments */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Attachments</h4>
              <div className="space-y-4">
                <div>
                  <input
                    id="files"
                    name="files"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Select additional files to attach
                  </p>
                </div>
                
                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">New files to upload:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {files.map((file, index) => (
                        <li key={index} className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          {file.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div id="feedback"></div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default SupplierNoticeEdit;
